// Pure url logic for the tenant's client list, mirroring ClientListResolver.cs on the
// server. Both surfaces resolve the SAME configured value, so a disagreement here means the
// add-in's inactive-site indicator and document creation read different lists for a tenant.
// Deliberately free of side-effectful imports so it can be unit-tested on its own.

// The client list is a global inactive-site indicator source, independent of managed sites. It
// is a SharePoint site web url that hosts a list titled "Clients", unless the configured value
// names a specific list - a renamed or non-English list is addressed by url instead.
export const CLIENT_LIST_TITLE = "Clients";

const LISTS_SEGMENT = "/lists/";

// Locates the "/Lists/<name>" boundary in a url path. The LAST qualifying occurrence wins,
// because the hosting web can itself sit under a segment named "lists"
// (/sites/lists/Lists/Clients) and taking the first one there resolves the site as the list. An
// occurrence only counts when it is followed by a name segment that is not an .aspx page, which
// stops a folder named "lists" under a list from winning. nameEnd is -1 for a name-less
// ".../Lists/" - ambiguous input (equally a web whose last segment is "lists") that keeps the
// historic reading as a truncated list url.
const locateListSegment = (path: string): { separatorIndex: number; nameEnd: number } | undefined => {
  const lowerPath = path.toLowerCase();
  let separatorIndex = -1;
  let nameEnd = -1;
  let namelessSeparatorIndex = -1;

  for (let index = lowerPath.indexOf(LISTS_SEGMENT); index !== -1; index = lowerPath.indexOf(LISTS_SEGMENT, index + 1)) {
    const nameStart = index + LISTS_SEGMENT.length;
    const nextSlash = path.indexOf("/", nameStart);
    const end = nextSlash === -1 ? path.length : nextSlash;

    if (end <= nameStart) {
      namelessSeparatorIndex = index;
      continue;
    }
    if (lowerPath.substring(nameStart, end).endsWith(".aspx")) {
      continue;
    }
    separatorIndex = index;
    nameEnd = end;
  }

  if (separatorIndex === -1) {
    return namelessSeparatorIndex === -1 ? undefined : { separatorIndex: namelessSeparatorIndex, nameEnd: -1 };
  }
  return { separatorIndex, nameEnd };
};

// Normalises the configured client list url down to the hosting web url. Accepts the plain site
// url, a ".../Lists/Clients" list url, or a ".../AllItems.aspx" view url. Returns undefined for
// anything unparseable so a misconfigured value can never break site loading.
export const normalizeClientListWebUrl = (clientListUrl: string): string | undefined => {
  try {
    const parsed = new URL(clientListUrl.trim());
    let path = parsed.pathname;
    const located = locateListSegment(path);
    if (located) {
      path = path.substring(0, located.separatorIndex);
    } else if (path.toLowerCase().endsWith(".aspx")) {
      path = path.substring(0, path.lastIndexOf("/"));
    }
    if (path.endsWith("/")) {
      path = path.slice(0, -1);
    }
    return `${parsed.origin}${path}`;
  } catch {
    return undefined;
  }
};

// The decoded server-relative url of the list the configured value names, or undefined for a
// bare site url. Resolving THAT list survives renames and non-English titles; the title
// convention is only the fallback.
export const extractServerRelativeListUrl = (clientListUrl: string): string | undefined => {
  try {
    const path = decodeURIComponent(new URL(clientListUrl.trim()).pathname);
    const located = locateListSegment(path);
    return located && located.nameEnd > 0 ? path.substring(0, located.nameEnd) : undefined;
  } catch {
    return undefined;
  }
};

// The _api selector for the resolved list, relative to "_api/web/". Slashes stay literal (IIS
// rejects %2F inside a path) while spaces and other specials are encoded, and an OData string
// literal escapes a single quote by doubling it.
export const buildListEndpoint = (serverRelativeListUrl: string | undefined): string =>
  serverRelativeListUrl
    ? `GetList('${encodeURIComponent(serverRelativeListUrl.replace(/'/g, "''")).replace(/%2F/gi, "/")}')`
    : `lists/getbytitle('${CLIENT_LIST_TITLE}')`;

// The key every client-list session cache uses for a list: its web and selector, lower-cased,
// so a list url and a view url of the same list share one entry. Undefined for an unusable value.
export const buildClientListKey = (clientListUrl: string): string | undefined => {
  const webUrl: string | undefined = normalizeClientListWebUrl(clientListUrl);
  return webUrl
    ? `${webUrl.toLowerCase()}|${buildListEndpoint(extractServerRelativeListUrl(clientListUrl)).toLowerCase()}`
    : undefined;
};
